import { useTranslation } from 'react-i18next';
import { formatDayLabel } from '../../core/time/roundDate';
import type { RoundTotal } from './todayViewModel';

interface DayRowProps {
    roundDate: string;
    // The previous game day is shown: the label says "Eilen" instead of the date.
    previous?: boolean;
    // Tapping the label switches between the current and the previous game day
    // (none: the label is plain text).
    onToggleDay?: () => void;
    total: RoundTotal;
    // The point tracker is open in place of everything below this row.
    trackerOpen: boolean;
    onToggleTracker(): void;
}

const GaugeIcon = () => (
    <svg width="18" height="12" viewBox="0 0 18 12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M1 11a8 8 0 0 1 16 0" />
        <path d="M9 11l3.5-5" />
    </svg>
);

// The round's date (or "Eilen") and, at the other end, the point tracker's
// button next to the round's points total.
export function DayRow({ roundDate, previous = false, onToggleDay, total, trackerOpen, onToggleTracker }: DayRowProps) {
    const { t } = useTranslation();
    const dayLabel = previous ? t('today_yesterday') : formatDayLabel(roundDate, t);

    return (
        <div className="day-row">
            <h2 className="day-label">
                {onToggleDay === undefined
                    ? dayLabel
                    : (
                        <button type="button" className="day-label-button" aria-pressed={previous} onClick={onToggleDay} data-testid="day-label">
                            {dayLabel}
                        </button>
                    )}
            </h2>
            <button
                type="button"
                className={`tracker-button${trackerOpen ? ' on' : ''}`}
                aria-label={t('tracker_title')}
                aria-pressed={trackerOpen}
                onClick={onToggleTracker}
                data-testid="tracker-button"
            >
                <GaugeIcon />
            </button>
            <span
                className={`day-total${total.points === undefined ? ' off' : ''}`}
                data-testid="round-total"
            >
                {!total.locked && <span className="visually-hidden">{t('today_totalLabel')} </span>}
                {total.locked ? '' : total.points === undefined ? '–' : t('points_short', { n: total.points })}
            </span>
        </div>
    );
}
